import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ArrowLeft, BookMarked, Trash2, MessageSquare, Trophy, HelpCircle,
  FileText, Calendar, Star, Gift, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import errMsg from '../utils/apiError';

const TYPE_META = {
  message:  { label: 'Messages',   icon: MessageSquare, color: 'text-blue-600' },
  victory:  { label: 'Victoires',  icon: Trophy,        color: 'text-yellow-600' },
  question: { label: 'Questions',  icon: HelpCircle,    color: 'text-purple-600' },
  resource: { label: 'Ressources', icon: FileText,      color: 'text-emerald-600' },
  event:    { label: 'Événements', icon: Calendar,      color: 'text-orange-600' },
  story:    { label: 'Stories',    icon: Star,          color: 'text-pink-600' },
  reward:   { label: 'Récompenses',icon: Gift,          color: 'text-amber-600' },
};
const ALL_TYPES = Object.keys(TYPE_META);

export default function MonCarnetPage() {
  const [bookmarks, setBookmarks] = useState([]);
  const [counts, setCounts] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const navigate = useNavigate();
  const { api } = useAuth();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [listRes, countsRes] = await Promise.all([
        api.get('/bookmarks'),
        api.get('/bookmarks/counts'),
      ]);
      setBookmarks(listRes.data || []);
      setCounts(countsRes.data || { total: 0 });
    } catch (error) {
      toast.error(errMsg(error, 'Erreur de chargement'));
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = async (id) => {
    try {
      await api.delete(`/bookmarks/${id}`);
      setBookmarks((prev) => prev.filter((b) => b.id !== id));
      setCounts((c) => ({ ...c, total: Math.max(0, (c.total || 0) - 1) }));
      toast.success('Retiré de ton Carnet');
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const openBookmark = (bm) => {
    if (bm.item_url) {
      navigate(bm.item_url);
    }
  };

  const filtered = tab === 'all' ? bookmarks : bookmarks.filter((b) => b.item_type === tab);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <BookMarked className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="mon-carnet-page">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BookMarked className="w-6 h-6 text-primary" />
              Mon Carnet
            </h1>
            <p className="text-sm text-muted-foreground">
              Retrouve tout ce que tu as mis de côté — {counts.total || 0} élément{(counts.total || 0) > 1 ? 's' : ''} sauvegardé{(counts.total || 0) > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {counts.total === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <BookMarked className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-40" />
              <h3 className="font-heading text-lg mb-2">Ton Carnet est vide</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Clique sur l&apos;icône <BookMarked className="inline w-3 h-3 mx-1" /> près d&apos;un message, d&apos;une victoire ou d&apos;une question pour la mettre de côté.
                Retrouve-la ici, prête à te rappeler ce qui a compté.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex flex-wrap h-auto justify-start gap-1">
              <TabsTrigger value="all" data-testid="carnet-tab-all">
                Tout ({counts.total || 0})
              </TabsTrigger>
              {ALL_TYPES.map((t) => {
                const meta = TYPE_META[t];
                const n = counts[t] || 0;
                if (n === 0) return null;
                const Icon = meta.icon;
                return (
                  <TabsTrigger key={t} value={t} data-testid={`carnet-tab-${t}`}>
                    <Icon className="w-4 h-4 mr-1" />
                    {meta.label} ({n})
                  </TabsTrigger>
                );
              })}
            </TabsList>

            <TabsContent value={tab} className="mt-4 space-y-3">
              {filtered.map((bm) => {
                const meta = TYPE_META[bm.item_type] || { label: bm.item_type, icon: BookMarked, color: 'text-muted-foreground' };
                const Icon = meta.icon;
                return (
                  <Card key={bm.id} className="card-base" data-testid={`bookmark-${bm.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-secondary/50 flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">{meta.label}</Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(bm.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <p className="font-medium truncate">{bm.item_title}</p>
                          {bm.item_preview && (
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{bm.item_preview}</p>
                          )}
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-1">
                          {bm.item_url && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openBookmark(bm)}
                              title="Ouvrir"
                              data-testid={`carnet-open-${bm.id}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => removeBookmark(bm.id)}
                            title="Retirer du Carnet"
                            data-testid={`carnet-delete-${bm.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
